import { useState, useRef, memo } from 'react';
import { Stack, TextInput, Paper, Group, Text, Switch, UnstyledButton, Box, Badge } from '@mantine/core';
import { IconHash, IconUsers, IconClock, IconCheck } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useGameHistory } from '../../hooks/useGameHistory';
import { useBotActions } from '../BotContext';
import { notifications } from '@mantine/notifications';

/**
 * SetupStep component for Step 0 of the bot setup wizard.
 * Handles Game PIN entry and Team Mode toggle.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.form - Current form state.
 * @param {Function} props.setForm - Function to update form state.
 * @param {Object} props.errors - Current form errors.
 * @param {Function} props.setErrors - Function to update form errors.
 * @param {Function} props.handleKeyDown - Key down event handler.
 * @param {Object} props.variants - Motion variants for animation.
 */
const SetupStep = memo(({ form, setForm, errors, setErrors, handleKeyDown, variants }) => {
    const { history, formatRelativeTime } = useGameHistory();
    const { setSetupStep } = useBotActions();
    const pinInputRef = useRef(null);
    const [selectedId, setSelectedId] = useState(null);

    /**
     * Handles restoring a previous configuration from history.
     * Updates name, amount, and teamMode but preserves the current PIN.
     * @param {Object} item - The history entry to restore.
     * @param {number} index - The index of the item to mark as selected.
     */
    const handleRestore = (item, index) => {
        setSelectedId(index);
        setForm(prev => ({
            ...prev,
            name: item.name,
            amount: item.amount,
            teamMode: item.teamMode
        }));

        const isPinPresent = form.pin.trim().length > 0;

        if (isPinPresent) {
            notifications.show({
                title: 'Ready to Launch!',
                message: 'PIN and Settings synchronized.',
                color: 'green',
                icon: <IconCheck size={16} />,
                autoClose: 2500,
            });
            setSetupStep(2);
        } else {
            notifications.show({
                title: 'Settings Applied',
                message: 'Enter a PIN to continue.',
                color: 'blue',
                icon: <IconCheck size={16} />,
                autoClose: 2500,
            });
            
            // Stay at step 0 and focus the PIN input
            setSetupStep(0);
            pinInputRef.current?.focus();
        }
    };

    return (
        <motion.div
            key="step0"
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ width: '100%', position: 'absolute' }}
        >
            <Stack>
                <TextInput
                    ref={pinInputRef}
                    label="Game Pin"
                    placeholder="123456"
                    autoFocus
                    leftSection={<IconHash size={16} />}
                    value={form.pin}
                    onChange={(e) => {
                        setForm({ ...form, pin: e.target.value });
                        setErrors({ ...errors, pin: null });
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'next')}
                    error={errors.pin}
                    required
                />

                <Paper withBorder p="md" radius="md" bg="dark.6">
                    <Group justify="space-between">
                        <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm" fw={500}>Team Mode</Text>
                            <Text size="xs" fw={500}>(only if game in Teams Mode)</Text>
                        </Group>
                        <Switch
                            checked={form.teamMode}
                            onChange={(e) => setForm({ ...form, teamMode: e.currentTarget.checked })}
                            size="md"
                        />
                    </Group>
                </Paper>

                {history.length > 0 && (
                    <Box mt="xs">
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={8} ml={2} ls={0.5}>Quick Start</Text>
                        <Stack gap={8}>
                            {history.slice(0, 1).map((item, index) => {
                                const isSelected = selectedId === index;
                                return (
                                    <UnstyledButton
                                        key={index}
                                        onClick={() => handleRestore(item, index)}
                                        component={motion.button}
                                        whileHover={{ scale: 1.02, backgroundColor: isSelected ? 'rgba(34, 139, 230, 0.15)' : 'var(--mantine-color-dark-5)' }}
                                        whileTap={{ scale: 0.98 }}
                                        animate={{ 
                                            borderColor: isSelected ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-dark-4)',
                                            backgroundColor: isSelected ? 'rgba(34, 139, 230, 0.1)' : 'var(--mantine-color-dark-6)'
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid',
                                            display: 'block',
                                            textAlign: 'left',
                                            transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                        }}
                                    >
                                        <Group justify="space-between" align="center" wrap="nowrap">
                                            <Box>
                                                <Group gap={8} wrap="nowrap">
                                                    <Text size="sm" fw={700} c={isSelected ? 'blue.3' : 'white'} truncate>{item.name}</Text>
                                                    <Badge size="xs" variant={isSelected ? "filled" : "outline"} color="blue">{item.amount} Bots</Badge>
                                                </Group>
                                                <Group gap={6} mt={2} wrap="nowrap">
                                                    <Text size="10px" c="dimmed">Last PIN: {item.pin}</Text>
                                                    <Text size="10px" c="dark.3" fw={900}>·</Text>
                                                    <Group gap={3} wrap="nowrap">
                                                        <IconClock size={10} style={{ opacity: 0.5 }} />
                                                        <Text size="10px" c="dark.3">{formatRelativeTime(item.timestamp)}</Text>
                                                    </Group>
                                                </Group>
                                            </Box>
                                            <IconCheck 
                                                size={16} 
                                                color="var(--mantine-color-blue-6)"
                                                style={{ 
                                                    opacity: isSelected ? 1 : 0.2,
                                                    transition: 'opacity 0.2s ease'
                                                }} 
                                            />
                                        </Group>
                                    </UnstyledButton>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </motion.div>
    );
});

export default SetupStep;
